import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { notification } from "antd";
import {
  FinetuneRunNew,
  FinetuneRunStatus,
} from "../../../model_gen/finetuning";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { PlusIcon } from "lucide-react";
import FinetuneRunsTable from "./FinetuneRunsTable";

import moment from "moment";

export const getTimeRemaining = (record: FinetuneRunNew) => {
  if (record.status === "RUNNING" && record.progress && record.date_created) {
    const startTime = moment(record.date_created);
    const now = moment();
    const elapsedMinutes = now.diff(startTime, "minutes");

    if (record.progress > 0 && elapsedMinutes > 0) {
      // Calculate total time based on progress so far
      const totalEstimatedMinutes = elapsedMinutes / record.progress;
      const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

      if (remainingMinutes <= 0) {
        return "Almost complete";
      }

      if (remainingMinutes < 60) {
        return `${Math.ceil(remainingMinutes)} minutes remaining`;
      } else {
        const hours = Math.floor(remainingMinutes / 60);
        const mins = Math.ceil(remainingMinutes % 60);
        return `${hours}h ${mins}m remaining`;
      }
    }
    return "Calculating...";
  }
  return null;
};

interface SortConfig {
  key: keyof FinetuneRunNew | null;
  direction: "asc" | "desc";
}

const FinetuneRuns: React.FC = () => {
  const { apiKey } = usePermissions();
  const [searchParams] = useSearchParams();
  const [finetuneRuns, setFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRuns, setTotalRuns] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState<FinetuneRunStatus[]>(
    () => {
      // Get all status[] parameters from URL
      const statusParams = searchParams.getAll("status[]");
      return statusParams as FinetuneRunStatus[];
    }
  );
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const pageSize = 10;
  const [datasets, setDatasets] = useState<{ [key: number]: any }>({});

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all datasets - we'll use limit 1000 to ensure we get most datasets
    DatasetService.getDatasets(0, 1000, setIsLoading, apiKey).subscribe(
      (response: { items: any[]; total: number }) => {
        // Create a map of datasets by ID for quick lookup
        const datasetsMap = response.items.reduce((acc, dataset) => {
          acc[dataset.id] = dataset;
          return acc;
        }, {});
        setDatasets(datasetsMap);
      },
      (error: any) => {
        console.error("Failed to load datasets", error);
        notification.error({
          message: "Error",
          description: "Failed to load datasets information",
        });
      }
    );
  }, [apiKey]);

  const handleSort = (key: keyof FinetuneRunNew) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedFinetuneRuns = React.useMemo(() => {
    const sortableRuns = [...finetuneRuns];
    if (sortConfig.key) {
      sortableRuns.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      sortableRuns.sort((a, b) => {
        const dateA = a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    return sortableRuns;
  }, [finetuneRuns, sortConfig]);

  const handleRunClick = (run: FinetuneRunNew) => {
    navigate(`/forge/finetuning/${run.id}`);
  };



  useEffect(() => {
    // Initial data fetch - show loading indicator
    refreshFinetuneRuns(true, 1);
  }, [apiKey]); // Only trigger on apiKey changes

  // Add effect for status filter changes
  useEffect(() => {
    // Reset to first page and refresh with page 1 explicitly
    setCurrentPage(1);
    refreshFinetuneRuns(true, 1);
  }, [selectedStatuses]); // Trigger when status filter changes

  // Create a function to check if there are running jobs
  const hasRunningJobs = () => {
    return finetuneRuns.some(
      (run) =>
        run.status.toUpperCase() === "RUNNING" ||
        run.status.toUpperCase() === "SCHEDULED"
    );
  };

  // Separate useEffect for the polling logic
  useEffect(() => {
    const pollingInterval = 60000;

    const intervalId = setInterval(() => {
      // Don't show loading indicator for background polling, and only poll if not currently loading
      if (!isLoading) {
        refreshFinetuneRuns(false, currentPage);
      }
    }, pollingInterval);

    // Clean up interval on component unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [apiKey, hasRunningJobs(), currentPage, selectedStatuses, isLoading]); // Add isLoading to dependencies

  const refreshFinetuneRuns = (showLoading = true, page = currentPage) => {
    const offset = (page - 1) * pageSize;
    
    if (showLoading) {
      setIsLoading(true);
    }
    
    FinetuneRunService.getFinetuneRuns(
      offset,
      pageSize,
      null, // Don't use the service's loading handler, manage it ourselves
      apiKey,
      selectedStatuses.length > 0 ? selectedStatuses : undefined
    ).subscribe(
      (response: { items: FinetuneRunNew[]; total: number }) => {
        setFinetuneRuns(response.items);
        setTotalRuns(response.total);
        if (currentPage !== page) {
          setCurrentPage(page);
        }
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
        notification.error({
          message: "Error",
          description: "Failed to load finetune runs",
        });
      }
    );
  };

  // Update URL when status filters change
  useEffect(() => {
    const queryString =
      selectedStatuses.length > 0
        ? "?" + selectedStatuses.map((status) => `status[]=${status}`).join("&")
        : "";
    navigate(`${window.location.pathname}${queryString}`, { replace: true });
  }, [selectedStatuses, navigate]);

  const totalPages = Math.ceil(totalRuns / pageSize);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Finetune Runs</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Finetune Runs
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage and monitor your model fine-tuning runs
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filters and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Select 
              value={selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined} 
              onValueChange={(value) => {
                if (value) {
                  setSelectedStatuses(value.split(",") as FinetuneRunStatus[]);
                } else {
                  setSelectedStatuses([]);
                }
              }}
            >
              <SelectTrigger className="w-80">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="EXPORTING">Exporting</SelectItem>
                <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                <SelectItem value="RUNNING">Running</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="VALIDATING">Validating</SelectItem>
                <SelectItem value="DELETED">Deleted</SelectItem>
              </SelectContent>
            </Select>
            {selectedStatuses.length > 0 && (
              <button
                onClick={() => setSelectedStatuses([])}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
          <Button
            onClick={() => navigate("/forge/finetuning/new")}
            className="gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            New Finetune Run
          </Button>
        </div>

        {/* Table */}
        <FinetuneRunsTable
          finetuneRuns={sortedFinetuneRuns}
          loading={isLoading}
          datasets={datasets}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRunClick={handleRunClick}
          onRefresh={() => refreshFinetuneRuns(false)}
          apiKey={apiKey}
          setIsLoading={setIsLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRuns}
          itemsPerPage={pageSize}
          onPageChange={(page) => {
            if (page !== currentPage) {
              refreshFinetuneRuns(true, page);
            }
          }}
        />

      </div>
    </div>
  );
};

export default FinetuneRuns;
